import React, { useRef, useState } from 'react';
import type { FC } from 'react';
import { ButtonBase, IconButton, Popover, SvgIcon, Tooltip, Typography, fade, makeStyles } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import { Moon as MoonIcon, Settings as SettingsIcon, Sun as SunIcon } from 'react-feather';
import clsx from 'clsx';
import useSettings from 'src/hooks/useSettings';
import { THEMES } from 'src/constants';
import type { Theme } from 'src/theme';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme: Theme) => ({
  popover: {
    width: 320,
    padding: theme.spacing(2.5),
    borderRadius: 12
  },
  hint: {
    marginTop: 2,
    fontSize: 13,
    color: theme.palette.text.secondary
  },
  label: {
    margin: theme.spacing(2.5, 0, 1),
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary
  },
  themes: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px'
  },
  themeCard: {
    flexDirection: 'column',
    gap: '8px',
    padding: theme.spacing(1.5, 1),
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
    fontSize: 13.5,
    fontWeight: 500,
    transition: 'border-color 120ms ease, background-color 120ms ease',
    '&:hover': { backgroundColor: theme.palette.action.hover },
    '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 }
  },
  themeOn: {
    borderColor: theme.palette.primary.main,
    backgroundColor: fade(theme.palette.primary.main, 0.08),
    color: theme.palette.text.primary,
    fontWeight: 600
  },
  // Mini preview of the theme's surface.
  swatch: {
    width: '100%',
    height: 34,
    borderRadius: 6,
    border: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  langs: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    overflow: 'hidden'
  },
  lang: {
    width: '100%',
    justifyContent: 'space-between',
    padding: theme.spacing(1.1, 1.5),
    fontSize: 14,
    color: theme.palette.text.primary,
    '&:hover': { backgroundColor: theme.palette.action.hover },
    '&:focus-visible': { backgroundColor: theme.palette.action.focus }
  },
  langOn: {
    fontWeight: 600,
    backgroundColor: theme.palette.action.selected
  },
  divider: {
    borderTop: `1px solid ${theme.palette.divider}`
  },
  foot: {
    marginTop: theme.spacing(2),
    fontSize: 12.5,
    color: theme.palette.text.secondary
  }
}));

// Each language in its own name, so anyone can find theirs.
const langs = [
  { id: 'en', name: 'English' },
  { id: 'fr', name: 'Français' },
  { id: 'es', name: 'Español' },
  { id: 'pt', name: 'Português' },
  { id: 'jp', name: '日本語' }
];

const Settings: FC = () => {
  const classes = useStyles();
  const ref = useRef<any>(null);
  const { settings, saveSettings } = useSettings();
  const [isOpen, setOpen] = useState(false);
  const { t, i18n } = useTranslation();

  // Changes apply straight away (no Save step) and are remembered.
  const setTheme = (theme: string) => saveSettings({ ...settings, theme });
  const setLang = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('lng', lang);
  };
  const current = (i18n.language || 'en').slice(0, 2);

  const themes = [
    // Each theme's own background colours (src/theme), as a tiny preview.
    { id: THEMES.LIGHT, label: t('Light'), Icon: SunIcon, surface: '#F2F5F7', ink: '#1A2027' },
    { id: THEMES.ONE_DARK, label: t('Dark'), Icon: MoonIcon, surface: '#0A1015', ink: '#E6E9ED' }
  ];

  return (
    <>
      <Tooltip title={t('Settings')}>
        <IconButton color="inherit" onClick={() => setOpen(true)} ref={ref} aria-label={t('Settings')}>
          <SvgIcon fontSize="small">
            <SettingsIcon />
          </SvgIcon>
        </IconButton>
      </Tooltip>
      <Popover
        // Opens upward: the gear sits in the sidebar footer.
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        classes={{ paper: classes.popover }}
        anchorEl={ref.current}
        onClose={() => setOpen(false)}
        open={isOpen}
      >
        <Typography variant="h5" component="h2" color="textPrimary">
          {t('Settings')}
        </Typography>
        <Typography className={classes.hint}>{t('How the dashboard looks for you.')}</Typography>

        <div className={classes.label} id="settings-theme">
          {t('Theme')}
        </div>
        <div className={classes.themes} role="radiogroup" aria-labelledby="settings-theme">
          {themes.map(({ id, label, Icon, surface, ink }) => (
            <ButtonBase
              key={id}
              role="radio"
              aria-checked={settings.theme === id}
              className={clsx(classes.themeCard, settings.theme === id && classes.themeOn)}
              onClick={() => setTheme(id)}
            >
              <span className={classes.swatch} style={{ backgroundColor: surface, color: ink }}>
                <Icon size={16} />
              </span>
              {label}
            </ButtonBase>
          ))}
        </div>

        <div className={classes.label} id="settings-language">
          {t('Language')}
        </div>
        <ul className={classes.langs} role="radiogroup" aria-labelledby="settings-language">
          {langs.map((l, i) => (
            <li key={l.id} className={i ? classes.divider : undefined}>
              <ButtonBase role="radio" aria-checked={current === l.id} className={clsx(classes.lang, current === l.id && classes.langOn)} onClick={() => setLang(l.id)}>
                <span lang={l.id === 'jp' ? 'ja' : l.id}>{l.name}</span>
                {current === l.id && <CheckIcon fontSize="small" color="primary" />}
              </ButtonBase>
            </li>
          ))}
        </ul>

        <Typography className={classes.foot}>{t('Changes apply right away and are remembered on this device.')}</Typography>
      </Popover>
    </>
  );
};

export default Settings;
